import { fn, hash } from '@ember/helper';
import { service } from '@ember/service';

import clipboard from 'ember-cli-clipboard/modifiers/clipboard';
import { t } from 'ember-intl';
import { not, or } from 'ember-truth-helpers';
import { trackedFunction } from 'reactiveweb/function';

import Component from 'umg-ember-backend/components/record-detail/forms';
import Button from 'umg-ember-backend/components/ui/button';
import Form from 'umg-ember-backend/components/ui/form';
import InfoBox from 'umg-ember-backend/components/ui/info-box';
import { findRecord } from 'umg-ember-backend/data/builders/umg-rest';
import { labelValueOptions } from 'umg-ember-backend/utils/intl';

const PRODUCT_TARGETS = [
  { linkPatternId: 146, value: 'amazon_physical' },
  { linkPatternId: 145, value: 'amazon_download' },
  { linkPatternId: 167, value: 'amazon_unlimited' },
  { linkPatternId: 143, value: 'itunes' },
  { linkPatternId: 163, value: 'apple' },
  { linkPatternId: 153, value: 'saturn' },
  { linkPatternId: 162, value: 'mediamarkt' },
  { linkPatternId: 152, value: 'spotify' },
  { linkPatternId: 149, value: 'googleplay' },
  { linkPatternId: 168, value: 'jpc' },
];

const PRODUCTSHOP_TARGETS = [
  { value: 'EMP' },
  { value: 'Artist Store' },
  { value: 'iTunes Sammellink' },
  { value: 'Weltbild' },
  { value: 'Chapter One' },
  { value: 'Spotify Pre-Save' },
  { value: 'MediaMarkt Sammellink' },
  { value: 'Saturn Sammellink' },
  { value: 'Amazon Sammellink' },
  { value: 'JPC Sammellink' },
  { value: 'CeDe' },
  { value: 'Ex Libris' },
  { value: 'shop24direct' },
  { value: 'Digster Pop' },
  { value: 'Flight 13' },
  { value: 'HHV' },
  { value: 'Musik für dich' },
  { value: 'Emso.de' },
  { value: 'Napster' },
  { value: 'Amazon Music' },
  { value: 'Artist Store (schwarzes Logo)' },
  { value: 'Mueller' },
  { value: 'bücher.de' },
  { value: 'Linkfire EN' },
  { value: 'Apple Music Pre Add' },
  { value: 'Deezer-Presave' },
  { value: 'Linkfire DE' },
  { value: 'Spotify Playlist' },
  { value: 'Apple Music Playlist' },
  { value: 'uDiscover' },
  { value: 'Deezer Black' },
  { value: 'Tidal' },
  { value: 'YouTube' },
];

export default class RecordDetailFormsShortUrlComponent extends Component {
  // Services
  @service data;
  @service flashMessages;
  @service intl;
  @service store;

  // Defaults
  contentTypes = ['product', 'productshop', 'custom'];

  // Getter and setter
  get contentTypeOptions() {
    return labelValueOptions.call(this, this.contentTypes, [
      'forms.short-url.options.contentType',
    ]);
  }

  get countryOptions() {
    return this.resolvedCountry.value ?? [];
  }

  get productTargetTypes() {
    let productTargetTypes = [];

    if (this.args.model.isProduct) {
      productTargetTypes = PRODUCT_TARGETS;
    } else if (this.args.model.isProductShop) {
      productTargetTypes = PRODUCTSHOP_TARGETS;
    }

    return productTargetTypes.map((target) => {
      let scope = 'forms.short-url.options.productTargetType';

      return {
        ...target,
        label: this.intl.t(`${scope}.${target.value}`),
      };
    });
  }

  get targetUrlTypeOptions() {
    let scope = 'forms.short-url-target.options.urlType';
    let targetUrlTypes = [];

    if (!this.args.model.isProductShop) {
      targetUrlTypes.push({
        label: this.intl.t(`${scope}.custom`),
        value: 'custom',
      });
    }

    if (this.args.model.isProductShop) {
      targetUrlTypes.push({
        label: this.intl.t(`${scope}.productShop`),
        value: 'productShop',
      });
    }

    if (this.args.model.isProduct) {
      targetUrlTypes.push({
        label: this.intl.t(`${scope}.pattern`),
        value: 'pattern',
      });
    }

    return targetUrlTypes;
  }

  get urls() {
    return [
      `https://umgt.de/${this.args.model.urlHash}`,
      `https://dgt.link/${this.args.model.urlHash}`,
      `https://stokedmp4.de/${this.args.model.urlHash}`,
      `https://jazzecho.link/${this.args.model.urlHash}`,
      `https://brvd.de/${this.args.model.urlHash}`,
      `https://circlemag.de/${this.args.model.urlHash}`,
      `https://bmhits.de/${this.args.model.urlHash}`,
    ];
  }

  get utmContents() {
    return this.resolvedUtmContent.value ?? [];
  }

  get utmMediums() {
    return this.resolvedUtmMedium.value ?? [];
  }

  get utmSources() {
    return this.resolvedUtmSource.value ?? [];
  }

  // Functions
  appendUtmToTargets = async (newValue) => {
    const targets = await this.args.model.targets;
    targets.map((item) => (item.appendUtm = newValue));
  };

  selectCountryCode = (target, option) => {
    target.countryCode = option.countryCode;
  };

  selectContentType = (option) => {
    const contentType = option?.value;

    this.args.model.contentType = contentType;
    this.args.model.productTargetType = null;

    if (contentType === 'custom') {
      this.args.model.product = null;
    }

    this.createOrUpdateTarget(contentType);
  };

  selectProductTargetType = async (target) => {
    this.args.model.productTargetType = target?.value;

    if (!target.linkPatternId) {
      return;
    }

    const { content } = await this.store.request(
      findRecord('link-pattern', target.linkPatternId),
    );
    const linkPattern = content.data;

    const targets = await this.args.model.targets;
    targets.map((item) => (item.linkPattern = linkPattern));
  };

  selectUrlType = (target, option) => {
    let urlType = option ? option.value : null;

    target.urlType = urlType;
    target.targetUrl = null;
  };

  async createOrUpdateTarget(contentType) {
    const urlTypeMap = {
      custom: 'custom',
      product: 'pattern',
      productshop: 'productShop',
    };
    const urlType = urlTypeMap[contentType];
    const targets = await this.args.model.targets;

    if (!targets.length) {
      let defaultTarget = this.store.createRecord('short-url-target', {
        countryCode: 'default',
        shortUrl: this.args.model,
        urlType: urlType,
      });

      // Add new target
      targets.push(defaultTarget);
    } else {
      targets.map((item) => (item.urlType = urlType));

      if (this.args.model.isProduct) {
        targets.map((item) => (item.targetUrl = null));
      }
    }
  }

  resolvedUtmContent = trackedFunction(this, async () => {
    return await this.data.getCategories({ 'filter[type]': 'UTM_CONTENT' });
  });

  resolvedUtmMedium = trackedFunction(this, async () => {
    return await this.data.getCategories({ 'filter[type]': 'UTM_MEDIUM' });
  });

  resolvedUtmSource = trackedFunction(this, async () => {
    return await this.data.getCategories({ 'filter[type]': 'UTM_SOURCE' });
  });

  resolvedCountry = trackedFunction(this, async () => {
    return await this.data
      .getCategories({ 'filter[type]': 'COUNTRY' })
      .then((result) => {
        let countries = result.slice().sort((a, b) => a.name > b.name);

        countries.splice(0, 0, {
          name: 'All countries',
          countryCode: 'default',
          identifier: 'all countries default',
        });

        return countries;
      });
  });

  // Template
  <template>
    <Form @model={{@model}} @showErrors={{@showErrors}} as |form|>
      <div class='o-layout'>
        <div class='o-layout__item u-1/1 u-1/2@tablet'>
          {{#if form.model.isNew}}
            <InfoBox @type='warning'>
              {{t 'record_detail.forms.short_url.tracking_hint' htmlSafe=true}}
            </InfoBox>
          {{else}}
            <InfoBox @type='warning'>
              <strong>{{t 'record_detail.forms.short_url.url_hint'}}</strong><br
              />
              {{#each this.urls as |url|}}
                <div class='o-layout u-margin-top-small'>
                  <div class='o-layout__item u-3/4'>
                    {{url}}
                  </div>
                  <div class='o-layout__item u-1/4 u-align-right'>
                    <Button
                      @label={{t 'record_detail.forms.short_url.copy'}}
                      @style='inline'
                      @size='small'
                      {{clipboard
                        text=url
                        onSuccess=(fn
                          this.flashMessages.success
                          'record_detail.forms.short_url.success'
                        )
                        onError=(fn
                          this.flashMessages.success
                          'record_detail.forms.short_url.error'
                        )
                      }}
                    />
                  </div>
                </div>
              {{/each}}
            </InfoBox>
          {{/if}}
          <form.fieldset @titleKey='general' as |fieldset|>
            <fieldset.field
              @property='cmsState'
              @type='select-value'
              @options={{this.cmsStateOptions}}
            />
            <fieldset.field
              @property='urlHash'
              @disabled={{not @model.isNew}}
            />
            <fieldset.field
              @property='contentType'
              @type='select-value'
              @options={{this.contentTypeOptions}}
              @onChange={{this.selectContentType}}
            />
            {{#if (or @model.isProduct @model.isProductShop)}}
              <fieldset.field
                @property='product'
                @type='belongs-to'
                @showValidations={{true}}
                @on={{hash openNewRecord=false}}
              />
              <fieldset.field
                @property='productTargetType'
                @type='select-value'
                @options={{this.productTargetTypes}}
                @onChange={{this.selectProductTargetType}}
                @showValidations={{true}}
              />
            {{/if}}
            <fieldset.field
              @property='internalRemarks'
              @type='text-editor'
              @textEditorPreset='minimal'
            />
          </form.fieldset>
        </div>
        <div class='o-layout__item u-1/1 u-1/2@tablet'>
          <form.fieldset @titleKey='utm' as |fieldset|>
            <fieldset.field @property='eventAction' />
            <fieldset.field
              @property='utmSource'
              @type='select-record'
              @options={{this.utmSources}}
            />
            <fieldset.field
              @property='utmMedium'
              @type='select-record'
              @options={{this.utmMediums}}
            />
            <fieldset.field
              @property='utmContent'
              @type='select-value'
              @selectPath='name'
              @labelProperty='name'
              @options={{this.utmContents}}
            />
            <fieldset.field @property='utmCampaign' />
            <fieldset.field
              @property='appendUtm'
              @type='checkbox'
              @checkboxAction={{this.appendUtmToTargets}}
            />
          </form.fieldset>
          <form.sections.meta @model={{form.model}} />
        </div>
      </div>
      {{#if form.model.contentType}}
        <form.fieldset @titleKey='targets' as |fieldset|>
          <fieldset.field
            @property='targets'
            @type='has-many'
            @on={{hash
              openNewRecord=this.newRecord
              removeRecord=this.deleteRelation
              openRecord=false
              openRecordList=false
            }}
            as |target|
          >
            <Form @model={{target}} as |subform|>
              <div class='o-layout'>
                <div class='o-layout__item u-1/1 u-3/12@tablet'>
                  {{#if this.countryOptions}}
                    <subform.field
                      @property='countryCode'
                      @type='select-value'
                      @options={{this.countryOptions}}
                      @isSearchEnabled={{true}}
                      @searchField='name'
                      @labelProperty='name'
                      @selectPath='countryCode'
                      @onChange={{fn this.selectCountryCode target}}
                    />
                  {{/if}}
                </div>
                <div class='o-layout__item u-1/1 u-2/12@tablet'>
                  <subform.field
                    @property='urlType'
                    @type='select-value'
                    @options={{this.targetUrlTypeOptions}}
                    @onChange={{fn this.selectUrlType target}}
                  />
                </div>
                <div class='o-layout__item u-1/1 u-7/12@tablet'>
                  <subform.field
                    @property='targetUrl'
                    @disabled={{target.isPattern}}
                    @placeholder={{if
                      target.isPattern
                      'Wird automatisch generiert'
                    }}
                  />
                </div>
              </div>
            </Form>
          </fieldset.field>
        </form.fieldset>
      {{/if}}
    </Form>
  </template>
}
